import { Timestamp } from "firebase/firestore";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { View, YStack } from "tamagui";
import { sanitizeForFirestore } from "../../../modules/core/sanitizeForFirestore";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { Label } from "../../../ui/Label";
import { EditorForm } from "../../../ui/YooptaEditor";
import { BusinessOffsetDateSelector } from "../ui/BusinessAdminDatePicker";
import { BusinessAdminTitleAndEmojiEdit } from "../ui/BusinessAdminEmojiPicker";
import type { BusinessReminderData } from "./BusinessReminderModuleModel";

export const BusinessReminderAdmin: React.FC<{
  data: BusinessReminderData;
  businessPlanStartingDate?: Timestamp | null;
  onSave: (data: BusinessReminderData) => void;
}> = observer((props) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState(toJS(props.data));

  return (
    <View>
      <Box gap="$medium">
        <BusinessAdminTitleAndEmojiEdit
          formValues={formValues}
          onChange={(f) =>
            setFormValues((values) => {
              values.emoji = f.emoji ?? null;
              values.title = f.title ?? null;
              return values;
            })
          }
        />

        <YStack gap="$small">
          <Label>{t("business:date")}</Label>
          <BusinessOffsetDateSelector
            startingDate={props.businessPlanStartingDate}
            offsetDays={formValues.settings?.offsetInDays ?? undefined}
            date={formValues.settings?.dateDue?.toDate()}
            onChange={(value) => {
              setFormValues((values) => {
                if ("offset" in value) {
                  values.settings = {
                    offsetInDays: value.offset,
                    dateDue: null,
                  };
                } else {
                  values.settings = {
                    offsetInDays: null,
                    dateDue: Timestamp.fromDate(value.date),
                  };
                }
                return values;
              });
            }}
          />
        </YStack>

        <YStack gap="$small">
          <Label>{t("business:description")}</Label>
          <View
            paddingHorizontal={23}
            paddingVertical={12}
            borderColor="$divider"
            borderWidth={1}
          >
            <EditorForm
              value={formValues.descriptionYooptaEditor}
              onSave={(data) => {
                setFormValues((values) => {
                  values.descriptionYooptaEditor =
                    sanitizeForFirestore(data) ?? null;
                  return values;
                });
              }}
              id={"description"}
            />
          </View>
        </YStack>
      </Box>
      <Button onPress={() => props.onSave(formValues)}>
        {t("business:save")}
      </Button>
    </View>
  );
});
